/**
 * Montserrat for public routes, and the stack those routes name.
 *
 * The faces are the same four @font-face rules as publicFonts.css
 * (Latin subset, weights 500/600/700/800, font-display: swap). This
 * module installs that sheet only while a public route is mounted and
 * removes it on the way out, so a signed-in page never requests the
 * woff2. A static CSS import would put the faces in the main bundle.
 */

import montserratLatin from "../../../assets/fonts/montserrat/montserrat-latin-wght-normal.woff2";

export const PUBLIC_FONT_FAMILY =
  "'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif";

const PUBLIC_FONT_ELEMENT_ID = "acertei-public-montserrat";

/**
 * The signed-in shell's Google stylesheet. Defined here because
 * `signedInTypeface.ts` already imports this module; the other direction
 * would be a cycle. `mountPublicFonts` removes that link so a public
 * route never leaves it in the document.
 */
export const SIGNED_IN_TYPEFACE_ELEMENT_ID = "acertei-signed-in-typeface";

const LATIN_RANGE =
  "U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD";

const WEIGHTS = [500, 600, 700, 800] as const;

/** The sheet PublicLayout inserts. One variable woff2, one face per weight. */
export function publicMontserratCss(fontUrl: string = montserratLatin): string {
  return WEIGHTS.map(
    (weight) => `@font-face {
  font-family: "Montserrat";
  font-style: normal;
  font-weight: ${weight};
  font-display: swap;
  src: url("${fontUrl}") format("woff2");
  unicode-range: ${LATIN_RANGE};
}`,
  ).join("\n\n");
}

let publicMounts = 0;

/** True from the moment a public route asks for Montserrat until the last one lets go. */
export function publicTypefaceIsActive(): boolean {
  return publicMounts > 0;
}

function installPublicFontSheet(css: string): void {
  let style = document.getElementById(PUBLIC_FONT_ELEMENT_ID);
  if (!(style instanceof HTMLStyleElement)) {
    style = document.createElement("style");
    style.id = PUBLIC_FONT_ELEMENT_ID;
    document.head.appendChild(style);
  }
  style.textContent = css;
}

/**
 * Insert the Montserrat faces. The returned function removes them when the
 * last public caller unmounts. Safe to call from more than one public view;
 * the sheet stays while any caller is still mounted.
 */
export function mountPublicFonts(): () => void {
  publicMounts += 1;
  document.getElementById(SIGNED_IN_TYPEFACE_ELEMENT_ID)?.remove();
  installPublicFontSheet(publicMontserratCss());

  let released = false;
  return () => {
    if (released) return;
    released = true;
    publicMounts = Math.max(0, publicMounts - 1);
    if (publicMounts === 0) {
      document.getElementById(PUBLIC_FONT_ELEMENT_ID)?.remove();
    }
  };
}
